import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faEnvelope, faLock, faSignInAlt, faEye, faEyeSlash, 
    faPhone, faShieldHeart
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from './apiConfig';
import { requestForToken } from './firebase-config';

const UserLogin = ({ setUser }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState(''); 
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');

        const isOnlyNumbers = /^\d+$/.test(email.replace(/\+/g, ''));
        if (isOnlyNumbers && email.length < 9) {
            setError(t('error_phone_short'));
            return;
        }
        if (password.length < 6) {
            setError(t('error_password_short'));
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/auth/login`, { 
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'ngrok-skip-browser-warning': 'true' 
                },
                body: JSON.stringify({ email: email, password: password })
            });

            const data = await res.json();

            if (res.ok) {
                if (data.token) {
                    localStorage.setItem('token', data.token);
                }
                localStorage.setItem('liber_user', JSON.stringify(data.user));
                requestForToken(data.user.id);

                if (typeof setUser === 'function') {
                    setUser(data.user);
                }
                navigate('/profile');
            } else {
                setError(data.message || data.error || t('error_login_failed'));
            }
        } catch (err) {
            console.error("Login error:", err);
            setError(t('error_server_connection'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-wrapper">
            <Helmet><title>{t('login_page_title')} | LIBER GROUP</title></Helmet>
            
            {/* Ավելացնում ենք ոճերը անմիջապես component-ի մեջ */}
            <style>{premiumStyles}</style>

            <div className="auth-container">
                <div className="auth-card">
                    <div className="auth-header">
                        <div className="logo-circle">
                            <FontAwesomeIcon icon={faShieldHeart} />
                        </div>
                        <h1>LIBER <span>GROUP</span></h1>
                        <p>{t('login_subtitle')}</p>
                    </div>

                    {error && <div className="auth-error animate-shake">{error}</div>}

                    <form onSubmit={handleLogin} className="auth-form">
                        <div className="field-group">
                            <label>{t('label_email_or_phone')}</label>
                            <div className="input-box">
                                <FontAwesomeIcon 
                                    icon={email.includes('@') ? faEnvelope : faPhone} 
                                    className="input-icon" 
                                />
                                <input 
                                    type="text" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t('placeholder_login_input')}
                                    required
                                />
                            </div>
                        </div>

                        <div className="field-group">
                            <div className="label-row">
                                <label>{t('label_password')}</label>
                                <Link to="/forgot-password" style={{fontSize: '12px', color: '#38bdf8', textDecoration: 'none'}}>
                                    {t('link_forgot_password')}
                                </Link>
                            </div>
                            <div className="input-box">
                                <FontAwesomeIcon icon={faLock} className="input-icon" />
                                <input 
                                    type={showPassword ? "text" : "password"} 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    required
                                />
                                <button 
                                    type="button" 
                                    className="eye-btn"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                        </div>

                        <button type="submit" className="submit-btn" disabled={loading}>
                            {loading ? <div className="spinner"></div> : <>{t('btn_login')} <FontAwesomeIcon icon={faSignInAlt} /></>}
                        </button>
                    </form>

                    <div className="auth-footer">
                        <p>
                            {t('no_account_text')} 
                            <Link to="/register">{t('link_register')}</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const premiumStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700&display=swap');

    .auth-wrapper {
        min-height: 100vh;
        background: #0f111a;
        background-image: 
            radial-gradient(at 0% 0%, rgba(56, 189, 248, 0.1) 0, transparent 50%), 
            radial-gradient(at 100% 100%, rgba(37, 99, 235, 0.1) 0, transparent 50%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        font-family: 'Plus Jakarta Sans', sans-serif;
    }

    .auth-container { width: 100%; max-width: 420px; animation: slideUp 0.6s ease-out; }

    @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

    .auth-card {
        background: rgba(30, 41, 59, 0.7);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 24px;
        padding: 40px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    }

    .auth-header { text-align: center; margin-bottom: 30px; }

    .logo-circle {
        width: 56px; height: 56px;
        background: linear-gradient(135deg, #38bdf8, #2563eb);
        border-radius: 16px;
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto 16px;
        font-size: 24px; color: #fff;
    }

    .auth-header h1 { color: #fff; font-size: 22px; font-weight: 700; margin: 0; letter-spacing: -0.5px; }
    .auth-header h1 span { color: #38bdf8; }
    .auth-header p { color: #94a3b8; font-size: 14px; margin-top: 8px; }

    .auth-error {
        background: rgba(244, 63, 94, 0.1);
        color: #fb7185;
        padding: 12px;
        border-radius: 12px;
        font-size: 13px;
        margin-bottom: 20px;
        text-align: center;
        border: 1px solid rgba(244, 63, 94, 0.2);
    }

    .animate-shake { animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both; }
    @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }

    .auth-form { display: flex; flex-direction: column; gap: 18px; }

    .field-group { display: flex; flex-direction: column; gap: 8px; }
    .field-group label { color: #e2e8f0; font-size: 13px; font-weight: 600; margin-left: 4px; }

    .label-row { display: flex; justify-content: space-between; align-items: center; }

    .input-box { position: relative; display: flex; align-items: center; }
    .input-box input {
        width: 100%;
        background: rgba(15, 23, 42, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.1);
        padding: 14px 14px 14px 44px;
        border-radius: 14px;
        color: #fff;
        font-size: 14px;
        transition: all 0.2s;
    }

    .input-box input:focus { border-color: #38bdf8; background: rgba(15, 23, 42, 0.9); box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1); }

    .input-icon { position: absolute; left: 16px; color: #64748b; font-size: 16px; pointer-events: none; }
    .eye-btn { position: absolute; right: 12px; background: none; border: none; color: #64748b; cursor: pointer; padding: 4px; transition: 0.2s; }
    .eye-btn:hover { color: #38bdf8; }

    .submit-btn {
        background: linear-gradient(135deg, #38bdf8, #2563eb);
        color: #fff; border: none;
        padding: 14px; border-radius: 14px;
        font-weight: 700; font-size: 15px;
        cursor: pointer; transition: 0.3s;
        margin-top: 10px;
        display: flex; align-items: center; justify-content: center; gap: 10px;
    }

    .submit-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.1); box-shadow: 0 8px 20px rgba(37, 99, 235, 0.4); }
    .submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }

    .auth-footer { margin-top: 25px; text-align: center; color: #94a3b8; font-size: 14px; }
    .auth-footer a { color: #38bdf8; font-weight: 600; text-decoration: none; margin-left: 5px; transition: 0.2s; }
    .auth-footer a:hover { text-decoration: underline; }

    .spinner {
        width: 18px; height: 18px;
        border: 2px solid rgba(255,255,255,0.3);
        border-radius: 50%;
        border-top-color: #fff;
        animation: spin 0.8s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
`;

export default UserLogin;